import { useCallback, useEffect, useMemo, useState } from 'react';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useUser from '@alltrails/context/hooks/useUser';
import { BaseStyleId, DynamicStyleId } from '@alltrails/maps/types/Styles';

type AdvancedMapsUpsellStyleId = Extract<
  BaseStyleId | DynamicStyleId,
  'highDefTerrain' | 'osGreatBritain' | 'privateLands' | 'publicLands' | 'slopeAngle'
>;

const ADVANCED_MAPS_UPSELL_TRIGGERS = {
  highDefTerrain: 'Select LiDAR Hillshade Overlay',
  osGreatBritain: 'OS Great Britain Maps',
  privateLands: 'Select Land Boundaries Overlay',
  publicLands: 'Select Land Boundaries Overlay',
  slopeAngle: 'Select Slope Angle Overlay'
} as const satisfies Record<AdvancedMapsUpsellStyleId, UpsellTrigger>;

const isAdvancedMapsUpsellStyleId = (styleId: BaseStyleId | DynamicStyleId): styleId is AdvancedMapsUpsellStyleId =>
  Object.hasOwn(ADVANCED_MAPS_UPSELL_TRIGGERS, styleId);

const useAdvancedMapLayersPeakValidation = () => {
  const user = useUser();

  const [activeUpsell, setActiveUpsell] = useState<DynamicStyleId | BaseStyleId | null>(null);
  const [hasShownUpsellModal, setHasShownUpsellModal] = useState(false);
  const [applyStylesCallback, setApplyStylesCallback] = useState<() => void>();
  // Retain the resolved trigger so it does not flip to the fallback while the modal animates closed
  // (clearing activeUpsell), which would otherwise re-fire logUpsellShown with the wrong trigger.
  const [advancedMapsUpsellTrigger, setAdvancedMapsUpsellTrigger] = useState<UpsellTrigger>('Select Land Boundaries Overlay');
  const showAdvancedMapsUpsellModal = activeUpsell !== null && isAdvancedMapsUpsellStyleId(activeUpsell);

  const onUpsellModalCloseRequest = useCallback(() => setActiveUpsell(null), []);

  const handleAdvancedMapLayersEntitlementCheck = useCallback((styleId: DynamicStyleId | BaseStyleId, applyStyles: () => void) => {
    if (isAdvancedMapsUpsellStyleId(styleId)) {
      setAdvancedMapsUpsellTrigger(ADVANCED_MAPS_UPSELL_TRIGGERS[styleId]);
    }
    setActiveUpsell(styleId);
    setHasShownUpsellModal(true);
    setApplyStylesCallback(() => applyStyles);
  }, []);

  useEffect(() => {
    if (hasShownUpsellModal && !activeUpsell) {
      if (user?.featureEntitlements?.includes('advanced_map_layers')) {
        applyStylesCallback?.();
      }
      setApplyStylesCallback(undefined);
    }
    // We only want to check this when the modal closes (activeUpsell becomes null).
    // The upsell modals currently do not support an onFailure/onSuccess callback, which would be preferred.
    // The activeUpsell close transition is the intended trigger; user/applyStylesCallback would re-run this on unrelated updates.
  }, [activeUpsell]);

  return useMemo(
    () => ({
      advancedMapsUpsellTrigger,
      handleAdvancedMapLayersEntitlementCheck,
      onUpsellModalCloseRequest,
      showAdvancedMapsUpsellModal
    }),
    [advancedMapsUpsellTrigger, handleAdvancedMapLayersEntitlementCheck, onUpsellModalCloseRequest, showAdvancedMapsUpsellModal]
  );
};

export default useAdvancedMapLayersPeakValidation;
